import {
  Box,
  Tabs,
  Tab,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import React, { useState, useMemo, useEffect } from "react";
import SearchBar from "../../components/SearchBar";
import SwitchToggle from "../../components/SwitchToggle";
import AddIcon from "@mui/icons-material/Add";
import AddYogurtType from "../../Modals/addYougurtType";
import {
  useGetYogurtTypeListQuery,
  useDeleteYogurtTypeMutation,
  useUpdateYogurtTypeStatusMutation,
} from "../../store/api/commonAPI";
import Loader from "../../constants/Loader";
import Pagination from "../../components/Panigation";
import DeleteConfirmationModal from "../../Modals/deleteConfirmation";
import { showToast, showError } from "../../constants/toasts";
import { PricingMethod } from "../../constants/enums";

const ManageYogurtType = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const limit = 10;
  const [openModal, setOpenModal] = useState(false);
  const [editYogurtType, setEditYogurtType] = useState<{
    id: string;
    name: string;
    pricingMethod: number;
  } | null>(null);

  // Reset to first page when search changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  // Fetch yogurt types from API
  const { data, isLoading, error, refetch } = useGetYogurtTypeListQuery({
    page,
    limit,
    search: searchTerm,
  });

  // Delete yogurt type mutation
  const [deleteYogurtType, { isLoading: isDeleting }] =
    useDeleteYogurtTypeMutation();

  // Update yogurt type status mutation
  const [updateYogurtTypeStatus, { isLoading: isUpdatingStatus }] =
    useUpdateYogurtTypeStatusMutation();

  // Extract yogurt types from API response
  const rows = useMemo(() => {
    if (data?.data?.yogurt && Array.isArray(data.data.yogurt)) {
      return data.data.yogurt.map((yogurt: any) => ({
        id: yogurt._id || yogurt.id,
        name: yogurt.name || "N/A",
        pricingMethod: yogurt.pricing || PricingMethod.PER_100G,
        isActive: yogurt.isActive ?? true,
        createdAt: yogurt.createdAt
          ? new Date(yogurt.createdAt).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : "N/A",
      }));
    }
    return [];
  }, [data]);

  // Get pricing method display text
  const getPricingMethodText = (method: number) => {
    switch (method) {
      case PricingMethod.PER_100G:
        return "Per 100g";
      case PricingMethod.SIZE_WISE:
        return "Size vise (S/M/L)";
      default:
        return "N/A";
    }
  };

  // Calculate total pages
  const totalCount = data?.data?.total || 0;
  const totalPages = Math.ceil(totalCount / limit);

  // Handle page change
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditYogurtType(null);
    refetch();
  };

  // Handle edit button click
  const handleEditClick = (row: {
    id: string;
    name: string;
    pricingMethod: number;
  }) => {
    setEditYogurtType({
      id: row.id,
      name: row.name,
      pricingMethod: row.pricingMethod,
    });
    setOpenModal(true);
  };

  // Delete confirmation modal state
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [yogurtTypeToDelete, setYogurtTypeToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // Handle delete button click - opens confirmation modal
  const handleDeleteClick = (id: string, name: string) => {
    setYogurtTypeToDelete({ id, name });
    setOpenDeleteModal(true);
  };

  // Handle delete confirmation - actually deletes the yogurt type
  const handleConfirmDelete = async () => {
    if (!yogurtTypeToDelete) return;

    try {
      const response = await deleteYogurtType({
        id: yogurtTypeToDelete.id,
      }).unwrap();

      // Show success message from API response or default message
      const successMessage =
        response?.message ||
        `Yogurt type "${yogurtTypeToDelete.name}" has been deleted successfully`;
      showToast(successMessage);

      // Refetch the list after successful deletion
      refetch();
      // Close modal and reset state
      setOpenDeleteModal(false);
      setYogurtTypeToDelete(null);
    } catch (error: any) {
      console.error("Error deleting yogurt type:", error);

      // Extract error message from API response or show default
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.data?.error ||
        error?.message ||
        `Failed to delete yogurt type "${yogurtTypeToDelete.name}". Please try again.`;

      showError(errorMessage);

      // Close modal on error so user can try again
      if (!isDeleting) {
        setOpenDeleteModal(false);
        setYogurtTypeToDelete(null);
      }
    }
  };

  // Handle delete modal close
  const handleCloseDeleteModal = () => {
    if (!isDeleting) {
      setOpenDeleteModal(false);
      setYogurtTypeToDelete(null);
    }
  };

  // Handle status toggle
  const handleStatusToggle = async (
    yogurtTypeId: string,
    yogurtTypeName: string,
    currentStatus: boolean
  ) => {
    const newStatus = !currentStatus;
    try {
      const response = await updateYogurtTypeStatus({
        id: yogurtTypeId,
        isActive: newStatus,
      }).unwrap();

      // Show success message from API response or default message
      const successMessage =
        response?.message ||
        `Yogurt type "${yogurtTypeName}" has been ${
          newStatus ? "activated" : "deactivated"
        } successfully`;
      showToast(successMessage);
      refetch();
    } catch (error: any) {
      console.error("Error updating yogurt type status:", error);

      // Extract error message from API response or show default
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.data?.error ||
        error?.message ||
        `Failed to ${
          newStatus ? "activate" : "deactivate"
        } yogurt type "${yogurtTypeName}". Please try again.`;

      showError(errorMessage);
      refetch(); // Revert UI state if update failed
    }
  };

  return (
    <>
      <Loader isLoad={isLoading || isDeleting || isUpdatingStatus} />
      <div className="main_title">
        <h1>Manage Yogurt Types</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -
          Yogurt Types
        </p>
      </div>

      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearchTerm(e.target.value)
              }
              onCross={() => setSearchTerm("")}
              searchTerm={searchTerm}
              setDebouncedSearchTerm={setSearchTerm}
              placeholder="Search..."
            />
          </div>
          <div className="right_s">
            <Button
              className="btn btn_primary"
              onClick={() => {
                setEditYogurtType(null);
                setOpenModal(true);
              }}
            >
              <AddIcon /> Add New Type
            </Button>
          </div>
        </div>

        <TableContainer className="table_container">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Type Name</TableCell>
                <TableCell>Pricing Method</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {error ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Error loading yogurt types. Please try again.
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    {isLoading ? "Loading..." : "No yogurt types found"}
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, index) => (
                  <TableRow hover key={row.id}>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>
                      {getPricingMethodText(row.pricingMethod)}
                    </TableCell>
                    <TableCell>{row.createdAt}</TableCell>
                    <TableCell>
                      <SwitchToggle
                        checked={row.isActive}
                        onChange={() =>
                          handleStatusToggle(row.id, row.name, row.isActive)
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <div className="table_actions v2">
                        <Button
                          className="btn btn_primary"
                          onClick={() =>
                            navigate(`/manage-yogurt-types/detail/${row.id}`)
                          }
                        >
                          Manage
                        </Button>
                        <IconButton onClick={() => handleEditClick(row)}>
                          <img src="/static/images/edit2_icon.svg" alt="Edit" />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDeleteClick(row.id, row.name)}
                        >
                          <img
                            src="/static/images/trash2_icon.svg"
                            alt="Delete"
                          />
                        </IconButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPages > 0 && (
          <div
            className="table_footer"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "15px 20px",
              borderTop: "1px solid #e0e0e0",
            }}
          >
            <p style={{ margin: 0, color: "#717171", fontSize: "14px" }}>
              Showing <strong>{(page - 1) * limit + 1}</strong> to{" "}
              <strong>{Math.min(page * limit, totalCount)}</strong> of{" "}
              <strong>{totalCount}</strong> entries
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <Pagination
                module={rows}
                page={page}
                onPageChange={handlePageChange}
                totalPages={totalPages}
                setPage={setPage}
                totalCount={totalCount}
                limit={limit}
              />
            </div>
          </div>
        )}
      </div>

      <AddYogurtType
        open={openModal}
        onClose={handleCloseModal}
        setOpen={setOpenModal}
        editYogurtType={
          editYogurtType
            ? {
                _id: editYogurtType.id,
                id: editYogurtType.id,
                name: editYogurtType.name,
                pricing: editYogurtType.pricingMethod,
                pricingMethod: editYogurtType.pricingMethod,
              }
            : null
        }
      />

      <DeleteConfirmationModal
        open={openDeleteModal}
        onClose={handleCloseDeleteModal}
        setOpen={setOpenDeleteModal}
        onConfirm={handleConfirmDelete}
        itemName={yogurtTypeToDelete?.name || ""}
        isLoading={isDeleting}
      />
    </>
  );
};

export default ManageYogurtType;
